<template>
  <div class="compare-round-card">
    <div v-if="!hasPayload" class="compare-empty-card">
      {{ $t('training.supervisor.compare_empty_side') || 'Selecciona una ronda para mostrar este lado.' }}
    </div>
    <template v-else>
      <div v-if="!hideMedia" class="compare-media-block">
        <YoutubePlayer
          v-if="videoUrl && isYoutubeUrl(videoUrl)"
          :video-url="videoUrl"
          :can-delete="false"
          :read-only="true"
        />
        <LocalVideoPlayer
          v-else-if="videoUrl"
          :video-url="videoUrl"
          :read-only="true"
        />
        <div v-else class="compare-no-video">
          {{ $t('training.supervisor.compare_no_video') || 'Sin video disponible' }}
        </div>
      </div>

      <div class="compare-meta-grid">
        <div class="compare-meta-row compare-meta-row--primary">
          <div class="compare-meta-card compare-meta-card--wide">
            <span class="compare-meta-label">{{ $t('training.assigned_judge') || 'Juez asignado' }}</span>
            <strong>{{ judgeName }}</strong>
          </div>
          <div class="compare-meta-card compare-meta-card--wide">
            <span class="compare-meta-label">{{ $t('training.supervisor.compare_duration') || 'Duración' }}</span>
            <strong>{{ formatDurationFromRound(round) }}</strong>
          </div>
        </div>

        <div class="compare-meta-row compare-meta-row--metrics">
          <div class="compare-meta-card compare-meta-card--metric">
            <span class="compare-meta-label">Score</span>
            <strong>{{ scoreValue }}</strong>
          </div>
          <div class="compare-meta-card compare-meta-card--metric">
            <span class="compare-meta-label">Variety</span>
            <strong>{{ varietyValue }}</strong>
          </div>
          <div class="compare-meta-card compare-meta-card--metric">
            <span class="compare-meta-label">Total style</span>
            <strong>{{ totalStyleValue }}</strong>
          </div>
          <div class="compare-meta-card compare-meta-card--metric">
            <span class="compare-meta-label">{{ $t('training.total_score') || 'Puntuación total' }}</span>
            <strong>{{ totalScore }}</strong>
          </div>
        </div>
      </div>

      <div class="compare-tricks-block">
        <div v-if="enrichedValidatedTricks.length" class="compare-trick-list">
          <div v-for="(trick, index) in enrichedValidatedTricks" :key="trickKey(trick, index)" class="compare-trick-wrapper">
            <div v-if="isCombo(trick)" class="compare-combo-card">
              <button type="button" class="compare-combo-header" @click="toggleCombo(trick.header.combo_id)">
                <span class="compare-combo-index">{{ trick.header.combo_id || 'C' }}</span>
                <span class="compare-combo-main">
                  <strong class="compare-combo-title">Combo</strong>
                  <span class="compare-combo-meta">{{ trick.header.total_tricks_combo || subTricks(trick).length }} tricks</span>
                </span>
                <span class="compare-combo-score">{{ comboScore(trick) }}</span>
                <i :class="isComboExpanded(trick.header.combo_id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="compare-combo-arrow"></i>
              </button>

              <div v-if="isComboExpanded(trick.header.combo_id)" class="compare-subtrick-list">
                <div
                  v-for="(subTrick, subIndex) in subTricks(trick)"
                  :key="trickKey(trick, index) + '-' + subIndex"
                  class="compare-trick-item compare-trick-item--sub"
                >
                  <div class="compare-trick-level" :class="levelClass(subTrick.level)">
                    {{ subTrick.level || '-' }}
                  </div>
                  <div class="compare-trick-main">
                    <div class="compare-trick-heading">
                      <strong class="compare-trick-name">{{ trickLabel(subTrick) }}</strong>
                      <span
                        v-if="changeBadge(subTrick, subIndex)"
                        class="compare-change-badge"
                        :class="changeBadge(subTrick, subIndex).variantClass"
                        :title="changeBadge(subTrick, subIndex).tooltip"
                      >
                        {{ changeBadge(subTrick, subIndex).label }}
                      </span>
                    </div>
                    <p class="compare-trick-subtitle">{{ trickShortName(subTrick) }}</p>
                    <div class="compare-trick-flags">
                      <span v-if="variationLabel(subTrick)" class="compare-flag compare-flag--variation">{{ variationLabel(subTrick) }}</span>
                      <span v-if="grabLabel(subTrick)" class="compare-flag compare-flag--grab">{{ grabLabel(subTrick) }}</span>
                      <span v-if="dubLabel(subTrick)" class="compare-flag compare-flag--dub">{{ dubLabel(subTrick) }}</span>
                    </div>
                  </div>
                  <strong class="compare-trick-score">{{ trickScore(subTrick) }}</strong>
                </div>
              </div>
            </div>

            <div v-else class="compare-trick-item">
              <div class="compare-trick-level" :class="levelClass(trick.level)">
                {{ trick.level || '-' }}
              </div>
              <div class="compare-trick-main">
                <div class="compare-trick-heading">
                  <strong class="compare-trick-name">{{ trickLabel(trick) }}</strong>
                  <span
                    v-if="changeBadge(trick, index)"
                    class="compare-change-badge"
                    :class="changeBadge(trick, index).variantClass"
                    :title="changeBadge(trick, index).tooltip"
                  >
                    {{ changeBadge(trick, index).label }}
                  </span>
                </div>
                <p class="compare-trick-subtitle">{{ trickShortName(trick) }}</p>
                <div class="compare-trick-flags">
                  <span v-if="variationLabel(trick)" class="compare-flag compare-flag--variation">{{ variationLabel(trick) }}</span>
                  <span v-if="grabLabel(trick)" class="compare-flag compare-flag--grab">{{ grabLabel(trick) }}</span>
                  <span v-if="dubLabel(trick)" class="compare-flag compare-flag--dub">{{ dubLabel(trick) }}</span>
                </div>
              </div>
              <strong class="compare-trick-score">{{ trickScore(trick) }}</strong>
            </div>
          </div>
        </div>
        <p v-else class="compare-muted">{{ $t('training.supervisor.compare_no_tricks') || 'Sin tricks validados' }}</p>

        <div v-if="diffEntries.length" class="compare-diff-list">
          <div v-for="(diff, diffIndex) in diffEntries" :key="diffKey(diff, diffIndex)" class="compare-diff-item">
            <div class="compare-diff-topline">
              <span class="compare-diff-pill" :class="diffPillClass(diff)">
                {{ diffActionLabel(diff) }}
              </span>
              <span class="compare-diff-editor" v-if="diffEditorName(diff)">{{ diffEditorName(diff) }}</span>
              <span class="compare-diff-date" v-if="diff.edited_at">{{ formatDiffTimestamp(diff.edited_at) }}</span>
            </div>

            <div class="compare-diff-body">
              <div v-if="showBeforeLine(diff)" class="compare-diff-line compare-diff-line--before">
                <span class="compare-diff-side">Antes</span>
                <span class="compare-diff-text">{{ buildDiffLine(diff, 'before') }}</span>
              </div>
              <div v-if="showAfterLine(diff)" class="compare-diff-line compare-diff-line--after">
                <span class="compare-diff-side">Ahora</span>
                <span class="compare-diff-text">{{ buildDiffLine(diff, 'after') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import YoutubePlayer from '@/services/YoutubePlayer.vue';
import LocalVideoPlayer from '@/services/LocalVideoPlayer.vue';

const GRAB_OPTIONS = [
  { id: 1, name: 'Blunt' },
  { id: 2, name: 'Boneless' },
  { id: 3, name: 'BuddhaGrab' },
  { id: 4, name: 'CanOpener' },
  { id: 5, name: 'ChristAir' },
  { id: 6, name: 'Crossover' },
  { id: 7, name: 'CrossoverSafety' },
  { id: 8, name: 'CrossoverTruckDriver' },
  { id: 9, name: 'Flat' },
  { id: 10, name: 'HalfSquirrelLef.' },
  { id: 11, name: 'HalfSquirrelRig.' },
  { id: 12, name: 'HighChair' },
  { id: 13, name: 'HorizontalTwist' },
  { id: 14, name: 'Japan' },
  { id: 15, name: 'KneelAir' },
  { id: 16, name: 'Layout' },
  { id: 17, name: 'Mute' },
  { id: 18, name: 'No grab' },
  { id: 19, name: 'Open' },
  { id: 20, name: 'Pike' },
  { id: 21, name: 'SafetyGrab' },
  { id: 22, name: 'Scratch' },
  { id: 23, name: 'Seatbelt' },
  { id: 24, name: 'Shifty' },
  { id: 25, name: 'SpreadEagle' },
  { id: 26, name: 'SquirrelGrab' },
  { id: 27, name: 'Superman' },
  { id: 28, name: 'TruckDriver' },
  { id: 29, name: 'Tuck' },
  { id: 30, name: 'TuckBlunt' },
  { id: 31, name: 'TuckedTwist' },
  { id: 32, name: 'Twist' },
  { id: 33, name: 'Yokohama' },
];

export default {
  name: 'CompareRoundCard',
  components: {
    YoutubePlayer,
    LocalVideoPlayer,
  },
  props: {
    payload: {
      type: Object,
      default: null,
    },
    side: {
      type: String,
      default: 'left',
    },
    hideMedia: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collapsedCombos: {},
    };
  },
  computed: {
    round() {
      return this.payload?.round || null;
    },
    videoUrl() {
      return this.round?.video_link || this.round?.videoUrl || '';
    },
    judgeName() {
      return this.formatPerson(this.round?.assigned_judge || this.round?.judge || null);
    },
    validatedTricks() {
      return Array.isArray(this.payload?.validatedTricks) ? this.payload.validatedTricks : [];
    },
    validatedTricksDiffs() {
      return Array.isArray(this.payload?.validated_tricks_diffs) ? this.payload.validated_tricks_diffs : [];
    },
    diffLookup() {
      return this.buildDiffIndexMap(this.validatedTricksDiffs);
    },
    diffEntries() {
      return [...this.validatedTricksDiffs].sort((left, right) => {
        const timeDelta = this.diffTimestamp(right) - this.diffTimestamp(left);
        if (timeDelta !== 0) return timeDelta;
        return Number(left?.index ?? 0) - Number(right?.index ?? 0);
      });
    },
    enrichedValidatedTricks() {
      return this.attachDiffsToTricks(this.validatedTricks, this.diffLookup);
    },
    scoreValue() {
      return this.formatMetricValue(this.round?.score, 3);
    },
    varietyValue() {
      const value = Number(this.round?.variety);
      return Number.isFinite(value) && value > 0 ? String(value) : '—';
    },
    totalStyleValue() {
      const value = String(this.round?.total_style || '').trim();
      return value || '—';
    },
    totalScore() {
      const value = Number.parseFloat(this.round?.total_score);
      return Number.isFinite(value) ? value.toFixed(2) : '—';
    },
    hasPayload() {
      return Boolean(this.round);
    },
  },
  methods: {
    formatPerson(person) {
      if (!person) return '—';
      const label = [person.name, person.surname].filter(Boolean).join(' ').trim();
      return label || person.email || (person.id ? `#${person.id}` : '—');
    },
    isYoutubeUrl(url) {
      return /(?:youtube\.com|youtu\.be)/i.test(String(url || ''));
    },
    formatDurationFromRound(round) {
      if (!round) return '—';
      const directSeconds = Number(round.duration_seconds || round.scoring_duration_seconds || 0);
      if (Number.isFinite(directSeconds) && directSeconds > 0) {
        return this.formatDuration(directSeconds);
      }

      const startedAt = round.started_scoring_at ? new Date(round.started_scoring_at) : null;
      const completedAt = round.completed_scoring_at ? new Date(round.completed_scoring_at) : null;
      if (startedAt && completedAt && !Number.isNaN(startedAt.getTime()) && !Number.isNaN(completedAt.getTime())) {
        const diffSeconds = Math.max(0, Math.round((completedAt.getTime() - startedAt.getTime()) / 1000));
        return this.formatDuration(diffSeconds);
      }

      return '—';
    },
    formatDuration(seconds) {
      const value = Number(seconds);
      if (!Number.isFinite(value) || value < 0) return '—';
      const mins = Math.floor(value / 60).toString().padStart(2, '0');
      const secs = Math.floor(value % 60).toString().padStart(2, '0');
      return `${mins}:${secs}`;
    },
    isCombo(trick) {
      return Boolean(trick && trick.header && Array.isArray(trick.body));
    },
    comboScore(trick) {
      const headerScore = Number.parseFloat(trick?.header?.total_score);
      if (Number.isFinite(headerScore) && headerScore > 0) {
        return headerScore.toFixed(2);
      }
      const total = this.subTricks(trick).reduce((sum, subTrick) => sum + (Number.parseFloat(subTrick?.score) || 0), 0);
      return total > 0 ? total.toFixed(2) : '—';
    },
    toggleCombo(comboId) {
      if (!comboId) return;
      this.collapsedCombos = {
        ...this.collapsedCombos,
        [comboId]: !this.collapsedCombos[comboId],
      };
    },
    isComboExpanded(comboId) {
      if (!comboId) return true;
      return this.collapsedCombos[comboId] !== false;
    },
    trickKey(trick, index) {
      return trick?.validated_trick_id || trick?.validated_trick_entry_id || trick?.trick_entry_id || trick?.id || `${this.side}-${index}`;
    },
    trickLabel(trick) {
      return trick?.name || trick?.trick_name || trick?.label || trick?.header?.name || 'Trick';
    },
    trickShortName(trick) {
      return trick?.short_name || trick?.shortName || '';
    },
    trickScore(trick) {
      const score = Number.parseFloat(trick?.score || trick?.total_score || trick?.header?.total_score);
      return Number.isFinite(score) ? score.toFixed(2) : '—';
    },
    subTricks(trick) {
      return Array.isArray(trick?.body) ? trick.body : [];
    },
    formatMetricValue(value, decimals = 2) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed.toFixed(decimals) : '—';
    },
    categoryLabel(trick) {
      const categoryMap = {
        1: 'Aerial',
        2: 'Aerial / Flip',
        3: 'Aerial / Switch',
        4: 'Dynamic',
        5: 'Dynamic / Flip',
        6: 'Dynamic / Flip / Switch',
        7: 'Dynamic / Switch',
      };
      return categoryMap[Number(trick?.category)] || '';
    },
    variationLabel(trick) {
      const variation = Number(trick?.variation);
      if (variation === 1) return 'FS';
      if (variation === 2) return 'BS';
      return '';
    },
    grabLabel(trick) {
      const grabId = Number(trick?.grab);
      if (!Number.isFinite(grabId) || grabId <= 0) return '';

      const found = GRAB_OPTIONS.find((item) => item.id === grabId);
      if (!found || found.name === 'No grab') return '';

      return found.name;
    },
    dubLabel(trick) {
      const dub = Number(trick?.dub);
      if (dub === 1) return '20%';
      if (dub === 2) return '50%';
      if (dub === 3) return '80%';
      return '';
    },
    levelClass(level) {
      const normalized = Number(level);
      return `compare-level--${Number.isFinite(normalized) ? normalized : 0}`;
    },
    buildDiffIndexMap(diffs) {
      const pickLatest = (candidate, existing) => {
        const candTs = new Date(candidate.edited_at || candidate.updated_at || candidate.created_at || 0).getTime();
        const currTs = new Date(existing.edited_at || existing.updated_at || existing.created_at || 0).getTime();
        if (!Number.isNaN(candTs) && !Number.isNaN(currTs)) {
          return candTs >= currTs ? candidate : existing;
        }
        return candidate;
      };

      const addKey = (acc, key, item) => {
        if (!key) return;
        const current = acc[key];
        acc[key] = current ? pickLatest(item, current) : item;
      };

      const toInt = (value) => {
        const parsed = Number(value);
        return Number.isInteger(parsed) ? parsed : null;
      };

      return (Array.isArray(diffs) ? diffs : []).reduce((acc, item) => {
        const idRaw = item?.trick_entry_id
          ?? item?.validated_trick_entry_id
          ?? item?.validated_trick_id
          ?? item?.trick_id
          ?? item?.after?.trick_entry_id
          ?? item?.before?.trick_entry_id
          ?? item?.after?.validated_trick_id
          ?? item?.before?.validated_trick_id
          ?? item?.after?.trick_id
          ?? item?.before?.trick_id
          ?? item?.id;
        const idxCandidates = [item?.index, item?.trick_index, item?.from_index, item?.index_before, item?.index_after]
          .map(toInt)
          .filter((value) => Number.isInteger(value));

        const idKey = idRaw !== undefined && idRaw !== null ? `id:${idRaw}` : null;
        addKey(acc, idKey, item);
        idxCandidates.forEach((idxVal) => addKey(acc, `idx:${idxVal}`, item));
        return acc;
      }, {});
    },
    attachDiffsToTricks(tricks, diffByKey) {
      const resolveTrickKeys = (trick, idx, allowIdx) => {
        const idVal = trick?.trick_entry_id
          ?? trick?.validated_trick_id
          ?? trick?.validated_trick_entry_id
          ?? trick?.trick_id
          ?? trick?.id
          ?? null;

        const idxVal = allowIdx && Number.isInteger(trick?.index)
          ? trick.index
          : (allowIdx && Number.isInteger(trick?.trick_index) ? trick.trick_index : (allowIdx && Number.isInteger(idx) ? idx : null));

        const idKey = idVal !== null ? `id:${idVal}` : null;
        const idxKey = allowIdx && Number.isInteger(idxVal) ? `idx:${idxVal}` : null;
        return { idKey, idxKey };
      };

      const attachDiff = (trick, idx, allowIdx = true) => {
        if (!trick || typeof trick !== 'object') return trick;

        const { idKey, idxKey } = resolveTrickKeys(trick, idx, allowIdx);
        const diff = (idKey && diffByKey[idKey]) || (idxKey && diffByKey[idxKey]) || null;
        const withDiff = diff ? { ...trick, __diff: diff } : { ...trick };

        if (Array.isArray(withDiff.body) && withDiff.header) {
          withDiff.body = withDiff.body.map((subTrick, subIdx) => {
            const fallbackIndex = subTrick?.index ?? subTrick?.trick_index ?? subIdx;
            return attachDiff(subTrick, fallbackIndex, false);
          });
        }

        return withDiff;
      };

      return (Array.isArray(tricks) ? tricks : []).map((trick, idx) => attachDiff(trick, idx, true));
    },
    resolveDiffMeta(trick, idxHint = null) {
      if (!trick || typeof trick !== 'object') return null;

      const toInt = (value) => {
        const parsed = Number(value);
        return Number.isInteger(parsed) ? parsed : null;
      };

      const idVal = trick?.trick_id
        ?? trick?.id
        ?? trick?.trick_entry_id
        ?? trick?.validated_trick_id
        ?? trick?.validated_trick_entry_id
        ?? null;
      const idKey = idVal !== null ? `id:${idVal}` : null;
      const idxVal = toInt(idxHint) ?? toInt(trick?.index) ?? toInt(trick?.trick_index);
      const idxKey = idxVal !== null ? `idx:${idxVal}` : null;
      const candidates = [trick.__diff, idKey ? this.diffLookup[idKey] : null, idxKey ? this.diffLookup[idxKey] : null].filter(Boolean);
      return candidates[0] || null;
    },
    normalizeAction(diffMeta) {
      const raw = String(diffMeta?.change_type || diffMeta?.action || diffMeta?.action_type || '').toLowerCase();
      if (raw === 'removed') return 'removed';
      if (raw === 'moved') return 'moved';
      if (raw === 'added') return 'added';
      return 'edited';
    },
    changeLabel(action) {
      if (action === 'added') {
        return this.$t('judging_program.change_badge_added') || 'Nuevo';
      }
      return this.$t('judging_program.change_badge_edited') || 'Editado';
    },
    changeBadge(trick, idxHint = null) {
      const diffMeta = this.resolveDiffMeta(trick, idxHint);
      if (!diffMeta) return null;
      const action = this.normalizeAction(diffMeta);
      if (action === 'moved' || action === 'removed') return null;

      return {
        label: this.changeLabel(action),
        variantClass: action === 'added' ? 'compare-change-badge--added' : 'compare-change-badge--edited',
        tooltip: this.buildChangeTooltip(diffMeta),
      };
    },
    buildChangeTooltip(diffMeta) {
      if (!diffMeta) return '';
      const editorName = diffMeta.editor
        ? `${diffMeta.editor.name || ''} ${diffMeta.editor.surname || ''}`.trim()
        : '';
      const parts = [];
      if (editorName) parts.push(editorName);
      if (diffMeta.edited_at) parts.push(this.formatDiffTimestamp(diffMeta.edited_at));
      return parts.join(' · ');
    },
    formatDiffTimestamp(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      const pad = (num) => String(num).padStart(2, '0');
      return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${String(date.getFullYear()).slice(-2)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    diffTimestamp(diff) {
      const raw = diff?.edited_at || diff?.updated_at || diff?.created_at;
      const parsed = Date.parse(raw || '');
      return Number.isFinite(parsed) ? parsed : 0;
    },
    diffKey(diff, index) {
      return diff?.trick_entry_id || diff?.validated_trick_id || `${this.side}-diff-${index}`;
    },
    diffActionLabel(diff) {
      const action = this.normalizeAction(diff);
      if (action === 'added') return this.$t('judging_program.change_badge_added') || 'Nuevo';
      if (action === 'removed') return 'Eliminado';
      if (action === 'moved') return 'Movido';
      return this.$t('judging_program.change_badge_edited') || 'Editado';
    },
    diffPillClass(diff) {
      const action = this.normalizeAction(diff);
      if (action === 'added') return 'compare-diff-pill--added';
      if (action === 'removed') return 'compare-diff-pill--removed';
      if (action === 'moved') return 'compare-diff-pill--moved';
      return 'compare-diff-pill--edited';
    },
    diffEditorName(diff) {
      const editor = diff?.editor || null;
      if (!editor) return '';
      return [editor.name, editor.surname].filter(Boolean).join(' ').trim();
    },
    showBeforeLine(diff) {
      const action = this.normalizeAction(diff);
      return action !== 'added' && Boolean(diff?.before);
    },
    showAfterLine(diff) {
      const action = this.normalizeAction(diff);
      return action !== 'removed' && Boolean(diff?.after);
    },
    extractTrickName(trick) {
      return trick?.trick_name || trick?.name || trick?.title || (this.$t('judge.trick') || 'Truco');
    },
    variationLabelFromValue(value) {
      const parsed = Number(value);
      if (parsed === 1) return 'FS';
      if (parsed === 2) return 'BS';
      return null;
    },
    grabValue(diff, side) {
      const source = side === 'before' ? diff?.before : diff?.after;
      return source?.grab ?? source?.grab_id ?? null;
    },
    dubValue(diff, side) {
      const source = side === 'before' ? diff?.before : diff?.after;
      return source?.dub ?? source?.dub_id ?? null;
    },
    extractScore(trick) {
      const value = trick?.score ?? trick?.total_score ?? null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    formatScoreValue(value) {
      if (!Number.isFinite(value)) return null;
      return Number.isInteger(value) ? String(value) : value.toFixed(2);
    },
    getIndexValue(diff, side) {
      const source = side === 'before' ? diff?.before : diff?.after;
      const nestedIndex = source?.index ?? source?.trick_index;
      const explicitBefore = diff?.index_before ?? diff?.indexBefore ?? diff?.from_index ?? diff?.fromIndex;
      const explicitAfter = diff?.index_after ?? diff?.indexAfter ?? diff?.to_index ?? diff?.toIndex;
      const topIndex = diff?.index ?? diff?.trick_index;
      const mappedIndex = side === 'before' ? explicitBefore : explicitAfter;
      const candidate = [nestedIndex, mappedIndex, topIndex]
        .map((value) => (value === undefined || value === null ? null : Number(value)))
        .find((value) => Number.isInteger(value));
      return Number.isInteger(candidate) ? candidate : null;
    },
    buildDiffLine(diff, side) {
      const source = side === 'before' ? (diff?.before || {}) : (diff?.after || {});
      const pieces = [];
      const trickName = this.extractTrickName(source);
      if (trickName) pieces.push(trickName);

      const variation = this.variationLabelFromValue(source?.variation ?? source?.variation_id);
      if (variation) pieces.push(variation);

      const grab = this.grabLabel({ grab: source?.grab ?? source?.grab_id });
      if (grab) pieces.push(grab);

      const dub = this.dubLabel({ dub: this.dubValue(diff, side) });
      const score = this.formatScoreValue(this.extractScore(source));
      const indexValue = this.getIndexValue(diff, side);

      const detailParts = [];
      if (score) detailParts.push(score);
      if (dub) detailParts.push(dub);
      if (Number.isInteger(indexValue)) detailParts.push(`#${indexValue + 1}`);

      return detailParts.length ? `${pieces.join(' ')} · ${detailParts.join(' · ')}` : pieces.join(' ');
    },
  },
};
</script>

<style scoped>
.compare-round-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  height: 100%;
}

.compare-empty-card,
.compare-no-video {
  min-height: 180px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #f8fafc;
  text-align: center;
  padding: 16px;
}

.compare-meta-grid {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.compare-meta-row {
  display: grid;
  gap: 10px;
}

.compare-meta-row--primary {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.compare-meta-row--metrics {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.compare-meta-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  min-width: 0;
}

.compare-meta-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.compare-meta-card strong {
  display: block;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compare-meta-card--metric {
  padding: 8px 10px;
}

.compare-tricks-block {
  margin-top: 14px;
}

.compare-trick-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compare-trick-wrapper {
  min-width: 0;
}

.compare-combo-card {
  border: 1px solid rgba(37, 102, 199, 0.2);
  border-radius: 14px;
  background: rgba(37, 102, 199, 0.08);
  overflow: hidden;
}

.compare-combo-header {
  width: 100%;
  border: none;
  background: transparent;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
}

.compare-combo-index,
.compare-trick-level {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  background: #64748b;
  flex-shrink: 0;
}

.compare-combo-main,
.compare-trick-main {
  min-width: 0;
}

.compare-combo-title {
  display: block;
  color: #0f172a;
  font-size: 14px;
}

.compare-combo-meta {
  display: block;
  color: #64748b;
  font-size: 12px;
}

.compare-combo-score,
.compare-trick-score {
  color: #0f172a;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.compare-combo-arrow {
  color: #64748b;
  font-size: 12px;
}

.compare-trick-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
}

.compare-trick-item--sub {
  border-radius: 10px;
  background: #fcfdff;
}

.compare-trick-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.compare-trick-name {
  color: #0f172a;
  min-width: 0;
}

.compare-trick-subtitle {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 12px;
}

.compare-trick-flags {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.compare-flag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
}

.compare-flag--category {
  background: rgba(37, 102, 199, 0.12);
  color: #1d4ed8;
}

.compare-flag--variation {
  background: rgba(100, 116, 139, 0.15);
  color: #334155;
}

.compare-flag--grab {
  background: rgba(250, 230, 41, 0.3);
  color: #713f12;
}

.compare-flag--dub {
  background: rgba(220, 38, 38, 0.12);
  color: #b91c1c;
}

.compare-change-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
}

.compare-change-badge--added {
  background: #16a34a;
  color: #fff;
}

.compare-change-badge--edited {
  background: #facc15;
  color: #422006;
}

.compare-level--1 {
  background: #2563eb;
}

.compare-level--2 {
  background: #16a34a;
}

.compare-level--3 {
  background: #ea580c;
}

.compare-level--4,
.compare-level--0 {
  background: #7c3aed;
}

.compare-subtrick-list {
  padding: 0 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.compare-muted {
  color: #64748b;
  margin: 0;
}

.compare-diff-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compare-diff-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  background: #f8fafc;
}

.compare-diff-topline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.compare-diff-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 700;
}

.compare-diff-pill--added {
  background: #dcfce7;
  color: #166534;
}

.compare-diff-pill--edited {
  background: #fef3c7;
  color: #92400e;
}

.compare-diff-pill--removed {
  background: #fee2e2;
  color: #991b1b;
}

.compare-diff-pill--moved {
  background: #dbeafe;
  color: #1d4ed8;
}

.compare-diff-editor,
.compare-diff-date {
  color: #64748b;
  font-size: 12px;
}

.compare-diff-body {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.compare-diff-line {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.compare-diff-side {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.compare-diff-line--before .compare-diff-side {
  color: #b45309;
}

.compare-diff-line--after .compare-diff-side {
  color: #047857;
}

.compare-diff-text {
  color: #0f172a;
  font-size: 12px;
  line-height: 1.45;
}

@media (max-width: 640px) {
  .compare-round-card {
    border-radius: 12px;
    padding: 10px;
  }

  .compare-empty-card,
  .compare-no-video {
    min-height: 120px;
    padding: 10px;
    font-size: 12px;
  }

  .compare-meta-grid {
    gap: 8px;
    margin-top: 10px;
  }

  .compare-meta-row,
  .compare-meta-row--primary,
  .compare-meta-row--metrics {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .compare-meta-card,
  .compare-trick-item {
    border-radius: 10px;
    padding: 8px;
  }

  .compare-meta-label,
  .compare-muted {
    font-size: 11px;
  }

  .compare-combo-header,
  .compare-trick-item {
    grid-template-columns: 30px minmax(0, 1fr) auto;
    gap: 8px;
  }

  .compare-combo-header {
    padding: 8px;
  }

  .compare-combo-index,
  .compare-trick-level {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }

  .compare-combo-title,
  .compare-trick-name,
  .compare-trick-score {
    font-size: 12px;
  }

  .compare-trick-subtitle,
  .compare-combo-meta,
  .compare-flag,
  .compare-change-badge,
  .compare-diff-editor,
  .compare-diff-date,
  .compare-diff-text {
    font-size: 10px;
  }

  .compare-diff-item {
    padding: 8px;
  }

  .compare-diff-line {
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 6px;
  }
}
</style>